import React, { useState, useEffect, useRef, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FileText, Download, Upload, Eye, Share, Trash2, Search, Filter,
  ArrowDown, Grid, List, Star, Clock, User, Folder, File, Image,
  FileSpreadsheet, FileImage, Archive, Plus, MoreHorizontal, Edit,
  Copy, ExternalLink, Send, CheckCircle, XCircle, Clock as ClockIcon,
  DollarSign, TrendingUp, Wallet, AlertCircle, Calendar,
} from "lucide-react";
import { ThemeContext } from "../../constant/theme";
import AlertModal from "../../components/AlertModal";
import { LanguageContext } from "../../constant/LanguageContext";
import ReusableDropdown from "../../components/ReusableDropdown";
import { apiFetch } from "../../context/AuthContext";

// ── translations ──────────────────────────────────────────────────────────────
import translations from './constants/translations';
// ── constants ─────────────────────────────────────────────────────────────────
import { getDocumentTypes } from "./constants/documentTypes";
import { getUrgencyLevels } from "./constants/urgencyLevels";

// ── utils ─────────────────────────────────────────────────────────────────────
import { formatCurrency, formatDate, formatFileSize, calculateTotalStorage } from "./utils/formatters";
import { getStatusColor, getStatusIcon, getFileIcon, getUrgencyColor } from "./utils/statusHelpers";
import { transformDocumentData } from "./utils/dataTransformers";
import { buildStyles } from "./utils/styles";
import { useResponsive } from "./utils/useResponsive";
import { useProfile } from "../../hooks/useProfile";

// ── components ────────────────────────────────────────────────────────────────
import QuickActions from "./components/QuickActions";
import StorageOverview from "./components/StorageOverview";
import Toolbar from "./components/Toolbar";
import CategoryFilter from "./components/CategoryFilter";
import DocumentsSection from "./components/DocumentsSection";
import AdvancementSection from "./components/AdvancementSection";
import UploadModal from "./components/UploadModal";
import DocViewModal from "./components/DocViewModal";
import DocRequestModal from "./components/DocRequestModal";
import AdvancementModal from "./components/AdvancementModal";
import RulesModal from "./components/RulesModal";
import TermsModal from "./components/TermsModal";
import RequestItem from "./components/RequestItem";
import DuplicateDocumentModal from "./components/DuplicateDocumentModal";
import { SkeletonStyles, SkeletonBox, LoadingPill } from "../../components/SkeletonLoader";

// ─────────────────────────────────────────────────────────────────────────────

const Mo3tiDocumentsPage = ({ employeeData, isDarkMode }) => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const t = translations[language];
  const isRTL = language === "ar";
  const screenSize = useResponsive();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { profile } = useProfile();

  // ── state ──────────────────────────────────────────────────────────────────
  const [documents, setDocuments] = useState([]);
  const [viewMode, setViewMode] = useState("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedType, setSelectedType] = useState("contract");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingLimits, setIsLoadingLimits] = useState(true);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateExistingDoc, setDuplicateExistingDoc] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestType, setRequestType] = useState("hr_letter");
  const [requestReason, setRequestReason] = useState("");
  const [documentRequests, setDocumentRequests] = useState([]);
  // Notification deep-link (?documentId=...): highlight + scroll to that request.
  const [highlightRequestId, setHighlightRequestId] = useState(null);
  const highlightConsumedRef = useRef(false);
  const [error, setError] = useState(null);
  const [requestRecipient, setRequestRecipient] = useState("");
  const [requestDetails, setRequestDetails] = useState("");
  const [requestSkipNotification, setRequestSkipNotification] = useState(false);
  const [showAdvancementModal, setShowAdvancementModal] = useState(false);
  const [advancementRequests, setAdvancementRequests] = useState([]);
  const [advancementStats, setAdvancementStats] = useState(null);
  const [advancementLimits, setAdvancementLimits] = useState(null);
  const [advancementValidation, setAdvancementValidation] = useState(null);
  const [alertModal, setAlertModal] = useState({
    open: false, title: "", message: "", variant: "info", showCancel: false, onConfirm: null,
  });
  const [showAdvancementRulesModal, setShowAdvancementRulesModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // ── Notification deep-link: once the requests list is loaded, highlight the
  // targeted request and scroll its card into view. ──────────────────────────
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const focusId = searchParams.get("documentId");
    if (!focusId || highlightConsumedRef.current) return;
    const match = documentRequests.find((r) => Number(r.id) === Number(focusId));
    if (match) {
      highlightConsumedRef.current = true;
      setHighlightRequestId(match.id);
      // Let the list render first, then scroll to the card.
      setTimeout(() => {
        document.getElementById(`docreq-${match.id}`)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentRequests, searchParams]);

  // ── Same pattern for advancement notifications (?advancementId=...) —
  // highlight the targeted advancement request card and scroll to it. ────────
  const [highlightAdvancementId, setHighlightAdvancementId] = useState(null);
  const advancementHighlightConsumedRef = useRef(false);
  useEffect(() => {
    const focusId = searchParams.get("advancementId");
    if (!focusId || advancementHighlightConsumedRef.current) return;
    const match = advancementRequests.find((r) => Number(r.id) === Number(focusId));
    if (match) {
      advancementHighlightConsumedRef.current = true;
      setHighlightAdvancementId(match.id);
      setTimeout(() => {
        document.getElementById(`advreq-${match.id}`)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancementRequests, searchParams]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [advancementForm, setAdvancementForm] = useState({
    requestedAmount: "",
    reason: "",
    urgency: "normal",
    numberOfInstallments: "",
    repaymentStartDate: "",
  });

  // ── derived constants ──────────────────────────────────────────────────────
  const documentTypes = getDocumentTypes(t);
  const urgencyLevels = getUrgencyLevels(t);

  const categories = [
    { id: "all", name: t.allDocuments, count: documents.length, icon: FileText },
    // { id: "contracts", name: t.contracts, count: documents.filter((d) => d.category === "contracts").length, icon: FileText },
    // { id: "payslips", name: t.payslips, count: documents.filter((d) => d.category === "payslips").length, icon: FileSpreadsheet },
    // { id: "certificates", name: t.certificates, count: documents.filter((d) => d.category === "certificates").length, icon: Star },
    // { id: "policies", name: t.policies, count: documents.filter((d) => d.category === "policies").length, icon: Folder },
    // Documents whose type isn't one of the four known categories above
    // (transformDocumentData falls back to "other") previously had nowhere
    // to show up except "All Documents" — no tab could ever find them.
    ...(documents.some((d) => d.category === "other")
      ? [{ id: "other", name: t.otherCategory || (isRTL ? "أخرى" : "Other"), count: documents.filter((d) => d.category === "other").length, icon: File }]
      : []),
  ];

  const styles = buildStyles(theme, screenSize, isRTL, getStatusColor, getUrgencyColor);

  // ── responsive resize ──────────────────────────────────────────────────────
  // handled inside useResponsive hook

  // ── handlers ───────────────────────────────────────────────────────────────
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
    }
  };

  const handleDocumentSelect = (docId) => {
    setSelectedDocuments((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  };

  const handleDocumentClick = (document) => {
    setSelectedDocument(document);
    setShowDocumentModal(true);
  };

  const handlePreview = (documentId) => {
    const token = localStorage.getItem("token");
    const url = `${BASE_URL}/api/documents/preview/${documentId}?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleUpload = async (customType) => {
    const finalType = typeof customType === "string" ? customType : selectedType;
    if (!selectedFile) {
      setAlertModal({
        open: true,
        title: language === "ar" ? "خطأ" : "Error",
        message: language === "ar" ? "يرجى اختيار ملف أولاً" : "Please select a file first",
        variant: "error",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    if (!finalType) {
      setAlertModal({
        open: true,
        title: language === "ar" ? "خطأ" : "Error",
        message: language === "ar" ? "يرجى تحديد نوع المستند" : "Please select a document type",
        variant: "error",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    // ── Duplicate check ───────────────────────────────────────
    try {
      const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      const checkRes = await apiFetch(`${BASE_URL}/api/documents/check/${infinityCode}/${finalType}`);
      if (checkRes.ok) {
        const checkData = await checkRes.json();
        if (checkData.exists) {
          setDuplicateExistingDoc(checkData.document);
          setShowDuplicateModal(true);
          return; // pause — user must confirm in modal
        }
      }
    } catch (_) { /* non-blocking */ }

    await doUpload(finalType);
  };

  const doUpload = async (customType) => {
    const finalType = typeof customType === "string" ? customType : selectedType;
    if (!selectedFile) {
      setAlertModal({
        open: true,
        title: language === "ar" ? "خطأ" : "Error",
        message: language === "ar" ? "يرجى اختيار ملف أولاً" : "Please select a file first",
        variant: "error",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    try {
      setIsUploading(true);
      const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("infinityCode", infinityCode);
      formData.append("type", finalType);

      const response = await apiFetch(`${BASE_URL}/api/documents/upload`, { method: "POST", body: formData });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      setShowUploadModal(false);
      setSelectedFile(null);
      setSelectedType("contract");
      setShowDuplicateModal(false);
      setDuplicateExistingDoc(null);
      await fetchDocumentRequests();
      setAlertModal({
        open: true,
        title: language === "ar" ? "تم الإرسال" : "Submitted",
        message: language === "ar"
          ? "تم رفع المستند بنجاح وهو الآن في انتظار موافقة المدير."
          : "Document uploaded and is now pending manager approval.",
        variant: "success",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      console.error("Upload failed:", err);
      const errorMsg = err.message || (language === "ar" ? "فشل تحميل المستند" : "Failed to upload document");
      setAlertModal({
        open: true,
        title: language === "ar" ? "خطأ" : "Error",
        message: errorMsg,
        variant: "error",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRequestDocument = async () => {
    try {
      const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      const employeeName = localStorage.getItem("employeeName") || employeeData?.sr;

      if (!infinityCode) {
        setError(language === "ar" ? "لم يتم العثور على رمز الموظف. يرجى تسجيل الدخول مرة أخرى." : "Employee code not found. Please log in again.");
        return;
      }

      const payload = {
        infinityCode,
        employeeName,
        requestType,
        reason: requestReason,
        recipient: requestRecipient || (language === "ar" ? "لمن يهمه الأمر" : "To Whom It May Concern"),
        details: requestDetails || "",
        skipConfirmationNotification: requestSkipNotification || false,
        clientName: profile?.clientName,
      };

      const response = await apiFetch(`${BASE_URL}/api/document-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      setShowRequestModal(false);
      setRequestReason("");
      setRequestRecipient("");
      setRequestDetails("");
      setRequestSkipNotification(false);
      setRequestType("hr_letter");
      fetchDocumentRequests();
      setAlertModal({
        open: true,
        title: language === "ar" ? "نجاح" : "Success",
        message: language === "ar" ? "تم إرسال طلب المستند بنجاح!" : "Document request submitted successfully!",
        variant: "success",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
    } catch (error) {
      console.error("Request failed:", error);
      const errorMessage = error.message || (language === "ar" ? "فشل إرسال الطلب" : "Failed to submit request");
      setError(errorMessage);
      setAlertModal({
        open: true,
        title: language === "ar" ? "خطأ" : "Error",
        message: errorMessage,
        variant: "error",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const handleAdvancementSubmit = async () => {
    const validation = advancementForm.numberOfInstallments
      ? await validateAdvancementRequest()
      : { isValid: true };

    if (validation === null) {
      setAlertModal({
        open: true,
        title: language === "ar" ? "فشل التحقق" : "Validation failed",
        message: language === "ar" ? "فشل التحقق من الطلب" : "Validation failed",
        variant: "error",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    if (!validation.isValid) {
      const translatedErrors = validation.errors.map((error) => {
        if (error.includes("You already have") && error.includes("active advancement")) {
          const match = error.match(/You already have (\d+) active advancement\(s\)\. Maximum allowed: (\d+)/);
          if (match) {
            return language === "ar"
              ? `لديك بالفعل ${match[1]} طلب سلفة نشط. الحد الأقصى المسموح: ${match[2]}`
              : error;
          }
        }
        return error;
      });
      setAlertModal({
        open: true,
        title: language === "ar" ? "لا يمكن إرسال الطلب" : "Cannot submit request",
        message: translatedErrors.join("\n\n"),
        variant: "error",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    const proceedWithSubmission = async () => {
      try {
        if (!validation.autoApprove && advancementLimits?.enabled && !termsAccepted) {
          setShowTermsModal(true);
          return;
        }

        const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
        const employeeName = localStorage.getItem("employeeName") || employeeData?.sr;

        if (!advancementForm.requestedAmount || !advancementForm.reason) {
          setAlertModal({
            open: true,
            title: language === "ar" ? "حقل مفقود" : "Missing fields",
            message: language === "ar" ? "يرجى ملء جميع الحقول المطلوبة" : "Please fill all required fields",
            variant: "error",
            showCancel: false,
            onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
          });
          return;
        }

        const response = await apiFetch(`${BASE_URL}/api/advancement-requests`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            infinityCode,
            employeeName,
            reason: advancementForm.reason,
            urgency: advancementForm.urgency,
            requestedAmount: parseFloat(advancementForm.requestedAmount),
            numberOfInstallments: advancementForm.numberOfInstallments
              ? parseInt(advancementForm.numberOfInstallments)
              : null,
            skipConfirmationNotification: advancementForm.skipConfirmationNotification || false,
          }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();

        setShowAdvancementModal(false);
        setTermsAccepted(false);
        setAdvancementValidation(null);
        setAdvancementForm({ requestedAmount: "", reason: "", urgency: "normal", numberOfInstallments: "", repaymentStartDate: "" });
        fetchAdvancementRequests();
        fetchAdvancementStats();

        const message =
          data?.autoApproved || data?.data?.autoApproved
            ? language === "ar" ? "تم إرسال وموافقة طلب السلفة تلقائياً!" : "Advancement request submitted and auto-approved!"
            : language === "ar" ? "تم إرسال طلب السلفة بنجاح!" : "Advancement request submitted successfully!";

        setAlertModal({
          open: true,
          title: language === "ar" ? "نجاح" : "Success",
          message,
          variant: "success",
          showCancel: false,
          onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
        });
      } catch (error) {
        console.error("Advancement request failed:", error);
        const errorMsg = error.message || (language === "ar" ? "فشل إرسال طلب السلفة" : "Failed to submit advancement request");
        setError(errorMsg);
        setAlertModal({
          open: true,
          title: language === "ar" ? "خطأ" : "Error",
          message: errorMsg,
          variant: "error",
          showCancel: false,
          onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
        });
      }
    };

    if (validation.warnings && validation.warnings.length > 0) {
      setAlertModal({
        open: true,
        title: language === "ar" ? "تحذيرات" : "Warnings",
        message: validation.warnings.join("\n") + "\n\n" + (language === "ar" ? "هل تريد المتابعة؟" : "Do you want to continue?"),
        variant: "info",
        showCancel: true,
        onConfirm: () => { setAlertModal((prev) => ({ ...prev, open: false })); proceedWithSubmission(); },
        onClose: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    await proceedWithSubmission();
  };

  // ── API calls ───────────────────────────────────────────────────────────────
  const fetchDocuments = async () => {
    try {
      const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      const response = await apiFetch(`${BASE_URL}/api/documents/${infinityCode}`, { method: "GET" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setDocuments((data?.data || data || []).map((d) => transformDocumentData(d, t)));
    } catch (err) {
      console.error("Failed to fetch documents:", err);
    }
  };

  const fetchDocumentRequests = async () => {
    try {
      const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      const response = await apiFetch(`${BASE_URL}/api/document-requests/employee/${infinityCode}`, { method: "GET" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setDocumentRequests(data?.data || data || []);
    } catch (err) {
      console.error("Failed to fetch requests:", err);
    }
  };

  const fetchAdvancementRequests = async () => {
    try {
      const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      const response = await apiFetch(`${BASE_URL}/api/advancement-requests/employee/${infinityCode}`, { method: "GET" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setAdvancementRequests(data?.data || data || []);
    } catch (err) {
      console.error("Failed to fetch advancement requests:", err);
    }
  };

  const fetchAdvancementStats = async () => {
    try {
      const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      const response = await apiFetch(`${BASE_URL}/api/advancement-requests/stats/${infinityCode}`, { method: "GET" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setAdvancementStats(data?.data || data || null);
    } catch (err) {
      console.error("Failed to fetch advancement stats:", err);
    }
  };

  const fetchAdvancementLimits = async () => {
    try {
      setIsLoadingLimits(true);
      let infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      if (!infinityCode) {
        setAdvancementLimits({ enabled: false, message: language === "ar" ? "لم يتم العثور على معلومات الموظف" : "Employee information not found" });
        return;
      }
      infinityCode = String(infinityCode).trim();
      const response = await apiFetch(`${BASE_URL}/api/advancement-requests/limits/${infinityCode}`, { method: "GET" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setAdvancementLimits(data?.data || data || { enabled: false });
    } catch (err) {
      console.error("Failed to fetch advancement limits:", err);
      setAdvancementLimits({ enabled: false, message: err.message || (language === "ar" ? "فشل تحميل قواعد السلفة" : "Failed to load advancement rules") });
    } finally {
      setIsLoadingLimits(false);
    }
  };

  const validateAdvancementRequest = async () => {
    try {
      const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      if (!advancementForm.requestedAmount) return null;

      const payload = {
        infinityCode,
        requestedAmount: parseFloat(advancementForm.requestedAmount),
        urgency: advancementForm.urgency || "normal",
      };

      if (advancementForm.numberOfInstallments) {
        payload.numberOfInstallments = parseInt(advancementForm.numberOfInstallments);
      }

      const response = await apiFetch(`${BASE_URL}/api/advancement-requests/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const validationResult = data?.data || data;
      setAdvancementValidation(validationResult);
      return validationResult;
    } catch (err) {
      console.error("Validation failed:", err);
      setError(err.message || (language === "ar" ? "فشل التحقق من الطلب" : "Validation failed"));
      return null;
    }
  };

  // ── initial page skeleton ─────────────────────────────────────────────────
  // Only gate the very first load — later refreshes (e.g. after upload) flip
  // isLoading again without re-showing the full-page skeleton over live data.
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (!isLoading) setPageReady(true);
  }, [isLoading]);

  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
        if (!infinityCode) { setIsLoading(false); return; }

        await fetchDocuments();
        await fetchDocumentRequests();
        await fetchAdvancementRequests();
        await fetchAdvancementStats();
        await fetchAdvancementLimits();
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError(language === "ar" ? "فشل تحميل البيانات" : "Failed to load data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [employeeData?.infinityCode, language]);

  // Real-time validation debounce
  useEffect(() => {
    if (advancementForm.requestedAmount && advancementForm.numberOfInstallments) {
      const timer = setTimeout(() => { validateAdvancementRequest(); }, 500);
      return () => clearTimeout(timer);
    } else {
      setAdvancementValidation(null);
    }
  }, [advancementForm.requestedAmount, advancementForm.numberOfInstallments, advancementForm.urgency]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (showAdvancementModal && advancementForm.requestedAmount && advancementForm.numberOfInstallments) {
        validateAdvancementRequest();
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [advancementForm.requestedAmount, advancementForm.numberOfInstallments, advancementForm.urgency, showAdvancementModal]);

  // ── derived values ─────────────────────────────────────────────────────────
  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch =
      (doc.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.tags || []).some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === "all" || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // The advancement (سلفة) feature is company-wide config, not per-request.
  // Keep the page usable for existing history even when the limits endpoint
  // reports the feature as disabled or temporarily unavailable.
  const advancementEnabled = isLoadingLimits || advancementLimits?.enabled !== false || advancementRequests.length > 0;

  const totalUsedMB = calculateTotalStorage(documents);
  const totalLimitMB = 30;
  const storageStats = {
    used: totalUsedMB,
    total: totalLimitMB,
    percentage: totalLimitMB > 0 ? Math.round((totalUsedMB / totalLimitMB) * 100 * 100) / 100 : 0,
  };

  // ── shared helpers to pass down ────────────────────────────────────────────
  const helpers = {
    formatCurrency: (amount) => formatCurrency(amount, language),
    formatDate: (dateString) => formatDate(dateString, language),
    getStatusColor: (status) => getStatusColor(status, t),
    getStatusIcon: (status) => getStatusIcon(status, t),
    getFileIcon,
    getUrgencyColor: (urgency) => getUrgencyColor(urgency, urgencyLevels),
  };

  // ── initial skeleton ───────────────────────────────────────────────────────
  if (!pageReady && isLoading) {
    return (
      <div className="employee-selfservice-root" style={styles.pageContainer}>
        <SkeletonStyles theme={theme} />
        <div style={{ ...styles.container, marginTop: "-30px", marginBottom: "20px" }}>
          <LoadingPill
            text={isRTL ? "جارٍ تحميل المستندات..." : "Loading documents information..."}
            theme={theme}
            isRTL={isRTL}
          />

          {/* Header skeleton */}
          <div style={styles.profileHeader}>
            <SkeletonBox width="220px" height="30px" />
          </div>

          {/* Toolbar skeleton */}
          <div style={{ ...styles.card1, display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
            <SkeletonBox width="280px" height="42px" style={{ borderRadius: "12px" }} />
            <SkeletonBox width="140px" height="42px" style={{ borderRadius: "12px" }} />
            <SkeletonBox width="140px" height="42px" style={{ borderRadius: "12px" }} />
          </div>

          {/* Category filter skeleton */}
          <div style={{ display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <SkeletonBox key={i} width="120px" height="38px" style={{ borderRadius: "12px" }} />
            ))}
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: screenSize.isMobile ? "1fr" : "2fr 1fr",
            gap: screenSize.isMobile ? "20px" : "28px",
          }}>
            {/* Documents grid skeleton */}
            <div style={styles.card1}>
              <SkeletonBox width="180px" height="26px" style={{ marginBottom: "20px" }} />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "16px" }}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <SkeletonBox key={i} width="100%" height="140px" style={{ borderRadius: "16px" }} />
                ))}
              </div>
            </div>

            {/* Requests list skeleton */}
            <div style={{ ...styles.card1, height: "600px" }}>
              <SkeletonBox width="160px" height="26px" style={{ marginBottom: "20px" }} />
              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                {[1, 2, 3, 4].map((i) => (
                  <SkeletonBox key={i} width="100%" height="64px" style={{ borderRadius: "12px" }} />
                ))}
              </div>
            </div>
          </div>

          {/* Advancement section skeleton */}
          <div style={{ ...styles.card1, marginTop: "20px" }}>
            <SkeletonBox width="220px" height="26px" style={{ marginBottom: "20px" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              {[1, 2, 3].map((i) => (
                <SkeletonBox key={i} width="100%" height="60px" style={{ borderRadius: "12px" }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="employee-selfservice-root" style={styles.pageContainer}>
      <div style={{ ...styles.container, marginTop: "-30px", marginBottom: "20px" }}>

        {/* Page Header */}
        <div style={styles.profileHeader}>
          <h1 style={styles.pageTitle}>{t.pageTitle}</h1>
        </div>

        {/* Quick Actions */}
        {/* <QuickActions t={t} styles={styles} scrollToSection={scrollToSection} /> */}

        {/* Storage Overview */}
        {/* <StorageOverview
          t={t}
          styles={styles}
          theme={theme}
          storageStats={storageStats}
          documents={documents}
        /> */}

        {/* Toolbar */}
        <Toolbar
          t={t}
          styles={styles}
          theme={theme}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setShowRequestModal={setShowRequestModal}
          setShowUploadModal={setShowUploadModal}
          setShowAdvancementModal={setShowAdvancementModal}
          showAdvancement={advancementEnabled}
        />

        {/* Category Filter and Actions */}
        {/* <div style={{ marginBottom: "20px" }}>
          <CategoryFilter
            categories={categories}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            styles={styles}
            theme={theme}
          >
            {advancementEnabled && (
              <button
                onClick={() => scrollToSection("advancement-requests-list")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 20px",
                  borderRadius: "12px",
                  border: theme === "dark" ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.1)",
                  backgroundColor: "transparent",
                  color: theme === "dark" ? "#fff" : "#1e293b",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  whiteSpace: "nowrap"
                }}
              >
                <ArrowDown size={18} />
                {t.mySolfaRequests}
              </button>
            )}
          </CategoryFilter>
        </div> */}

        <div style={{
          display: "grid",
          gridTemplateColumns: screenSize.isMobile ? "1fr" : "2fr 1fr",
          gap: screenSize.isMobile ? "20px" : "28px",
          alignItems: "start"
        }}>
          {/* Documents Section */}
          <DocumentsSection
            t={t}
            styles={styles}
            theme={theme}
            language={language}
            filteredDocuments={filteredDocuments}
            selectedDocuments={selectedDocuments}
            getFileIcon={getFileIcon}
            formatDate={helpers.formatDate}
            handleDocumentClick={handleDocumentClick}
          />

          {/* Document Requests */}
          <div style={{ ...styles.card1, height: "600px", display: "flex", flexDirection: "column" }}>
            <h3 style={styles.cardTitle}>
              <Send size={28} />
              {t.myRequests} ({documentRequests.length})
            </h3>
            {documentRequests.length === 0 ? (
              <div style={styles.emptyState}>
                <Send size={80} style={styles.emptyStateIcon} />
                <p style={styles.emptyStateText}>{t.noRequestsYet}</p>
              </div>
            ) : (
              <div style={{
                overflowY: "auto",
                flex: 1,
                paddingRight: "4px",
                scrollbarWidth: "none", // للـ Firefox
                msOverflowStyle: "none", // للـ IE و Edge
                WebkitOverflowScrolling: "touch", // للـ iOS
              }}>
                {/* Note: To hide Webkit scrollbars (Chrome/Safari), you typically need CSS: 
                    .container::-webkit-scrollbar { display: none; } 
                    But inline styles don't support pseudo-elements directly. 
                    Adding a global class or trusting standard scrollbarWidth is the React way. */}
                {documentRequests.map((request) => (
                  <RequestItem
                    key={request.id}
                    request={request}
                    styles={styles}
                    theme={theme}
                    getStatusIcon={helpers.getStatusIcon}
                    getStatusColor={helpers.getStatusColor}
                    formatDate={helpers.formatDate}
                    documentTypes={documentTypes}
                    t={t}
                    highlighted={Number(highlightRequestId) === Number(request.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Advancement Section */}
        <AdvancementSection

          t={t}
          styles={styles}
          theme={theme}
          language={language}
          highlightAdvancementId={highlightAdvancementId}
          advancementStats={advancementStats}
          advancementRequests={advancementRequests}
          advancementLimits={advancementLimits}
          isLoadingLimits={isLoadingLimits}
          urgencyLevels={urgencyLevels}
          formatCurrency={helpers.formatCurrency}
          formatDate={helpers.formatDate}
          getStatusColor={helpers.getStatusColor}
          getStatusIcon={helpers.getStatusIcon}
          getUrgencyColor={helpers.getUrgencyColor}
          setShowAdvancementRulesModal={setShowAdvancementRulesModal}
          setAlertModal={setAlertModal}
          advancementEnabled={advancementEnabled}
          storageElement={
            <StorageOverview
              t={t}
              styles={styles}
              theme={theme}
              storageStats={storageStats}
              documents={documents}
            />
          }
        />
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────────── */}

      {showUploadModal && (
        <UploadModal
          t={t}
          styles={styles}
          theme={theme}
          language={language}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          setShowUploadModal={setShowUploadModal}
          handleUpload={handleUpload}
          isUploading={isUploading}
          ReusableDropdown={ReusableDropdown}
        />
      )}

      {showDocumentModal && selectedDocument && (
        <DocViewModal
          t={t}
          styles={styles}
          theme={theme}
          selectedDocument={selectedDocument}
          setShowDocumentModal={setShowDocumentModal}
          handlePreview={handlePreview}
          formatDate={helpers.formatDate}
          screenSize={screenSize}
        />
      )}

      {showRequestModal && (
        <DocRequestModal
          t={t}
          styles={styles}
          theme={theme}
          language={language}
          isRTL={isRTL}
          documentTypes={documentTypes}
          requestType={requestType}
          setRequestType={setRequestType}
          requestDetails={requestDetails}
          setRequestDetails={setRequestDetails}
          requestRecipient={requestRecipient}
          setRequestRecipient={setRequestRecipient}
          requestReason={requestReason}
          setRequestReason={setRequestReason}
          requestSkipNotification={requestSkipNotification}
          setRequestSkipNotification={setRequestSkipNotification}
          setShowRequestModal={setShowRequestModal}
          handleRequestDocument={handleRequestDocument}
        />
      )}

      {showAdvancementModal && (
        <AdvancementModal
          t={t}
          styles={styles}
          theme={theme}
          language={language}
          isRTL={isRTL}
          advancementForm={advancementForm}
          setAdvancementForm={setAdvancementForm}
          advancementLimits={advancementLimits}
          advancementValidation={advancementValidation}
          setShowAdvancementModal={setShowAdvancementModal}
          handleAdvancementSubmit={handleAdvancementSubmit}
          formatCurrency={helpers.formatCurrency}
          setAlertModal={setAlertModal}
        />
      )}

      {showAdvancementRulesModal && advancementLimits && (
        <RulesModal
          t={t}
          styles={styles}
          theme={theme}
          language={language}
          isRTL={isRTL}
          advancementLimits={advancementLimits}
          setShowAdvancementRulesModal={setShowAdvancementRulesModal}
          setShowAdvancementModal={setShowAdvancementModal}
          formatCurrency={helpers.formatCurrency}
        />
      )}

      {showTermsModal && advancementLimits && (
        <TermsModal
          t={t}
          styles={styles}
          theme={theme}
          language={language}
          isRTL={isRTL}
          advancementLimits={advancementLimits}
          termsAccepted={termsAccepted}
          setTermsAccepted={setTermsAccepted}
          setShowTermsModal={setShowTermsModal}
          handleAdvancementSubmit={handleAdvancementSubmit}
          formatCurrency={helpers.formatCurrency}
        />
      )}


      {/* Alert Modal */}
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        showCancel={alertModal.showCancel}
        onConfirm={alertModal.onConfirm}
        onClose={() => setAlertModal((prev) => ({ ...prev, open: false }))}
        theme={theme}
        language={language}
      />

      {/* Duplicate document warning */}
      <DuplicateDocumentModal
        open={showDuplicateModal}
        onClose={() => { setShowDuplicateModal(false); setDuplicateExistingDoc(null); }}
        onConfirm={() => { setShowDuplicateModal(false); doUpload(); }}
        existingDoc={duplicateExistingDoc}
        newFile={selectedFile}
        selectedType={selectedType}
        language={language}
        theme={theme}
        onPreviewExisting={() => duplicateExistingDoc?.id && handlePreview(duplicateExistingDoc.id)}
      />
    </div>
  );
};

export default Mo3tiDocumentsPage;